import { useAuth } from '../../../lib/auth';
import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { Button } from '../../../components/buttons/Button.tsx';
import Alert from '../../../components/Alert.tsx';
import { useState } from 'react';
import {
    FormResultAlertProps,
    LicensePrice,
    SchemaDrivenFormFieldProps,
} from '../../../lib/types.ts';
import { useNavigate } from 'react-router';
import api, { API_URL } from '../../../lib/api.ts';
import { renderSchemaForm } from '../../../components/forms/renderSchemaForm.tsx';
import Skeleton from '../../../components/Skeleton.tsx';
import { cn, transformLaravelToFormikErrors } from '../../../lib/helpers.ts';
import VsimavecHeadEmotions from '../../../assets/vsimavec-head-emotions.svg';
import Tabs from '../../../components/tabs/Tabs.tsx';
import { useFetchInstitutionLicenses, useFetchLicensePrices } from '../../../lib/fetches.ts';
import { FeatureListItem } from '../../../components/lists/FeatureList.tsx';
import {
    UilBookOpen,
    UilCommentQuestion,
    UilFileAlt,
    UilRedo,
} from '@iconscout/react-unicons';

function CheckoutPriceTag({ licensePrice }: { licensePrice: LicensePrice }) {
    const { currency, duration, discount, price } = licensePrice;

    const durationToText = (duration: number) => {
        switch (duration) {
            case 365:
                return 'rok';
            case 30:
                return 'měsíc';
            default:
                return `${duration} dní`;
        }
    };

    return (
        <>
            <div className="flex items-center">
                <p className="flex items-baseline">
                    <span
                        className={cn(
                            'text-lg font-bold',
                            discount && 'text-gray-600 line-through'
                        )}
                    >
                        {price.toLocaleString('cs')}&nbsp;
                        {currency}
                    </span>
                    <span className="text-xs font-semibold text-gray-600">
                        &nbsp;/ {durationToText(duration)} / škola
                    </span>
                </p>
                {discount && (
                    <span className="ml-2 rounded bg-red-500 px-1.5 py-1 text-xs font-semibold text-white">
                        Nyní sleva {discount?.percentage}
                        &nbsp;%
                    </span>
                )}
            </div>
            {discount && (
                <p className="font-semibold text-red-500">
                    {discount?.price.toLocaleString('cs')}
                    &nbsp;
                    {currency}
                </p>
            )}
        </>
    );
}

function BuySection() {
    const { user, isLoading: isUserLoading } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'BuyLicensePage_BuySection',
    });

    const [formResult, setFormResult] = useState<FormResultAlertProps>({
        type: undefined,
        message: undefined,
    });

    const { data: licenses, isLoading } = useFetchInstitutionLicenses();
    const activeLicense = licenses?.active;

    const {
        handleSubmit,
        errors,
        touched,
        handleChange,
        values,
        isSubmitting,
        setSubmitting,
        setErrors,
        setValues,
        setFieldValue,
    } = useFormik({
        initialValues: {
            license_confirmation: false,
            billing_email: user?.institution?.billing_email ?? '',
        },
        enableReinitialize: true,
        onSubmit: (values) => {
            setFormResult({ type: undefined, message: undefined });

            api.post('/institution/buy/full-version', {
                ...values,
                license_price_id: licensePrices[selectedLicenseIndex].id,
            })
                .then((res) => {
                    setFormResult({
                        type: 'success',
                        message: res?.data,
                    });
                })
                .catch((err) => {
                    const message = err?.response?.data?.message;

                    setFormResult({
                        type: 'error',
                        message: message,
                    });

                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    const { data: licensePrices, isLoading: areLicensePricesLoading } =
        useFetchLicensePrices();

    const annualLicense = licensePrices?.[0];
    const monthlyLicense = licensePrices?.[1];

    const annualPrice = annualLicense?.discount?.price ?? annualLicense?.price;
    const monthlyPrice =
        monthlyLicense?.discount?.price ?? monthlyLicense?.price;

    const annualSavingsPercentage = areLicensePricesLoading
        ? null
        : annualPrice / (monthlyPrice * 12);

    const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(0);

    const schema: SchemaDrivenFormFieldProps[] = [
        // ---------------------------------------------------------------------
        {
            name: 'section-billing',
            type: 'html',
            label: <h3 className="mt-2">E-mail pro fakturaci</h3>,
        },
        {
            name: 'section-billing-sub',
            type: 'subheading',
            label: 'Na tento e-mail pošleme objednávku a fakturu. Uveďte prosím kontakt na člověka zodpovědného za finance.',
        },
        {
            name: 'billing_email',
            type: 'email',
            label: null,
            required: true,
            placeholder: 'ekonom@skola.cz',
        },
        // ---------------------------------------------------------------------
        {
            name: 'section-license',
            type: 'html',
            label: <h3 className="mt-2">Licence</h3>,
        },
        {
            name: 'license_confirmation',
            type: 'checkbox',
            label: (
                <span className="mb-2">
                    Zaškrtnutím výslovně souhlasím s podmínkami{' '}
                    <a
                        href={`${API_URL}/../files/vsimavec-licencni-smlouva-pro-skoly.pdf`}
                        target="_blank"
                        className="text-blue-500 underline"
                    >
                        licenční smlouvy
                    </a>
                    , kterou jsem si přečetl/a a bez výhrad ji přijímám.
                </span>
            ),
        },
        // ---------------------------------------------------------------------
        {
            name: 'section-promo',
            type: 'html',
            label: <h3 className="mt-2">Promo kód</h3>,
        },
        {
            name: 'section-promo-sub',
            type: 'subheading',
            label: 'Promo kód není povinný. Pokud jej nemáte, ponechte pole prázdné.',
        },
        {
            name: 'registration_code',
            type: 'text',
            label: null,
            required: false,
        },
        {
            name: 'price',
            type: 'html',
            label: (
                <div className="mt-2">
                    <Tabs
                        heading={<h3>Cena</h3>}
                        state={[selectedLicenseIndex, setSelectedLicenseIndex]}
                        tabs={[
                            {
                                id: 'annual',
                                label: areLicensePricesLoading
                                    ? 'Ročně'
                                    : licensePrices[0].name,
                                content: (
                                    <div>
                                        {!areLicensePricesLoading ? (
                                            <CheckoutPriceTag
                                                licensePrice={licensePrices[0]}
                                            />
                                        ) : (
                                            <Skeleton className="h-6 w-full rounded" />
                                        )}

                                        {!areLicensePricesLoading && (
                                            <p className="mt-1 text-sm text-gray-600">
                                                Při roční platbě Vás měsíc vyjde
                                                na{' '}
                                                {(
                                                    annualPrice / 12
                                                ).toLocaleString('cs')}
                                                &nbsp;Kč.
                                            </p>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                id: 'monthly',
                                label: areLicensePricesLoading
                                    ? 'Měsíčně'
                                    : licensePrices[1].name,
                                content: (
                                    <div>
                                        {!areLicensePricesLoading ? (
                                            <CheckoutPriceTag
                                                licensePrice={licensePrices[1]}
                                            />
                                        ) : (
                                            <Skeleton className="h-6 w-full rounded" />
                                        )}
                                        {!areLicensePricesLoading &&
                                            annualSavingsPercentage !==
                                                null && (
                                                <p className="mt-1 text-sm text-gray-600">
                                                    Při roční platbě Vás měsíc
                                                    vyjde jen na{' '}
                                                    {(
                                                        annualPrice / 12
                                                    ).toLocaleString('cs')}
                                                    &nbsp;Kč a ušetříte až{' '}
                                                    {(
                                                        (1 -
                                                            annualSavingsPercentage) *
                                                        100
                                                    ).toFixed()}
                                                    &nbsp;%.
                                                </p>
                                            )}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            {formResult.type && formResult.message && (
                <Alert type={formResult.type}>{formResult.message}</Alert>
            )}
            {formResult.type === 'success' ||
            user?.institution?.has_pending_invoice ? (
                <AlreadyBoughtSection />
            ) : null}
            {formResult.type !== 'success' &&
            !user?.institution?.has_pending_invoice ? (
                <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
                    <div className="mb-4">
                        <h3>Fakturační údaje</h3>
                        <p className="!mt-0 mb-2 block text-sm text-ink-800">
                            Pokud si přejete změnit fakturační udaje,
                            kontaktujte nás prosím na info@vsimavec.cz
                        </p>

                        <div className="mt-4 rounded-xl bg-cloud-200 pl-4">
                            <table className="w-full table-auto">
                                <tbody className="bg-white">
                                    <tr>
                                        <td className="w-auto border-b border-cloud-300 bg-cloud-200 py-3 pr-4 font-medium text-ink-800">
                                            Název&nbsp;instituce:
                                        </td>
                                        <td className="w-full border-b border-cloud-300 px-4 py-3">
                                            {user?.institution?.name}
                                            {isUserLoading && (
                                                <Skeleton className="h-6 w-full rounded" />
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="w-auto border-b border-cloud-300 bg-cloud-200 py-3 pr-4 font-medium text-ink-800">
                                            Adresa:
                                        </td>
                                        <td className="w-full border-b border-cloud-300 px-4 py-3">
                                            {!isUserLoading ? (
                                                <>
                                                    {user?.institution?.street}
                                                    <br />
                                                    {
                                                        user?.institution
                                                            ?.postal_code
                                                    }{' '}
                                                    {user?.institution?.city}
                                                </>
                                            ) : (
                                                <Skeleton
                                                    count={2}
                                                    className="h-6 w-full rounded"
                                                />
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="w-auto border-b border-cloud-300 bg-cloud-200 py-3 pr-4 font-medium text-ink-800">
                                            IČO:
                                        </td>
                                        <td className="w-full px-4 py-3">
                                            {user?.institution?.ico}
                                            {isUserLoading && (
                                                <Skeleton className="h-6 w-full rounded" />
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <form
                        className="flex flex-col space-y-4"
                        onSubmit={handleSubmit}
                    >
                        {schema.map(
                            renderSchemaForm(
                                values,
                                errors,
                                touched,
                                setFieldValue,
                                setValues,
                                handleChange
                            )
                        )}

                        {isLoading && <Skeleton className="h-6 w-full rounded" />}
                        {!isLoading && activeLicense && activeLicense.type !== 'Demo licence' && (
                            <p>Nová licence začne platit po vypršení současné licence, která je platná do {new Date(activeLicense.date_license_ends).toLocaleDateString('cs')}.</p>
                        )}
                        <Button
                            type="submit"
                            color="product"
                            className="mt-3 self-end"
                            loadingText="Probíhá změna"
                            isLoading={isSubmitting}
                        >
                            Závazně objednat
                        </Button>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

export function AlreadyBoughtSection() {
    return (
        <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
            <div className="flex flex-col items-center justify-center">
                <img
                    src={VsimavecHeadEmotions}
                    className="mb-4 w-32 text-center"
                    alt="Všímavec"
                />
                <h3 className="text-center">
                    Momentálně máte vytvořenou objednávku, kterou naleznete ve
                    svém e-mailu.
                </h3>
            </div>
        </div>
    );
}

export default function BuyLicensePage() {
    const { user, userHasFullLicense } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'BuyLicensePage',
    });

    // Only for contact person
    const navigate = useNavigate();
    if (!user?.is_institution_contact_person) {
        navigate('/');
    }

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'relative',
                    'mx-auto box-content py-9',
                    'max-w-2xl xl:max-w-6xl',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="mb-14">
                    <h1>Objednávka</h1>
                </div>
                <div>
                    <div
                        className={cn(
                            'mt-12 grid w-full gap-12 pb-32',
                            'max-w-3xl grid-cols-1 xl:max-w-none xl:grid-cols-2',
                            'xl:grid-cols-[1fr_420px]'
                        )}
                    >
                        <BuySection />
                        <div
                            className={cn(
                                'flex flex-col space-y-4',
                                'rounded-3xl p-8',
                                'self-start'
                            )}
                        >
                            <h2 className="mb-0 block text-2xl font-bold">
                                Co získáte plnou verzí?
                            </h2>

                            <ul className="space-y-6">
                                <FeatureListItem
                                    icon={UilBookOpen}
                                    title="100+ cvičení na celý rok"
                                />
                                <FeatureListItem
                                    icon={UilRedo}
                                    title="Pravidelné doplňování nového obsahu"
                                />
                                <FeatureListItem
                                    icon={UilFileAlt}
                                    title="Metodika pro učitele"
                                />
                                <FeatureListItem
                                    icon={UilCommentQuestion}
                                    title="Technická podpora"
                                />
                            </ul>
                        </div>
                    </div>
                </div>
            </main>
        </AuthLayout>
    );
}
